// Image gating utility: converts images marked as gated into a click-to-view UI.
// This is a frontend-only hook; a future integration can mark images as gated by
// setting data-gated="true" or by populating window.GATED_IMAGE_URLS (Set or array).

export function applyImageGating(rootElement = document) {
  try {
    // Collect images rendered by markdown with data-image-url
    const images = rootElement.querySelectorAll('img.user-image[data-image-url]');
    const gatedSet = normalizeGatedSet(window.GATED_IMAGE_URLS);

    images.forEach((img) => {
      const url = img.getAttribute('data-image-url') || '';
      const explicitlyGated = img.getAttribute('data-gated') === 'true';
      const shouldGate = explicitlyGated || (gatedSet && gatedSet.has(url));

      if (!shouldGate) return;

      // Skip if already wrapped
      if (img.closest('.gated-image')) return;

      const wrapper = document.createElement('div');
      wrapper.className = 'gated-image';
      wrapper.style.display = 'inline-block';
      wrapper.style.maxWidth = img.style.maxWidth || '100%';

      const gate = document.createElement('div');
      gate.className = 'gated-image-placeholder';
      gate.style.cursor = 'pointer';
      gate.style.color = 'var(--text-muted)';
      gate.style.fontStyle = 'italic';
      gate.style.padding = '8px 0';
      gate.textContent = '[Image has been flagged by another user. Click to view]';

      // Hide actual image initially
      img.style.display = 'none';

      gate.addEventListener('click', () => {
        gate.remove();
        img.style.display = '';
      });

      // Insert wrapper
      const parent = img.closest('.user-image-container');
      const imageLink = img.closest('a');
      parent.insertBefore(wrapper, imageLink);
      wrapper.appendChild(gate);
      wrapper.appendChild(imageLink);
    });
  } catch (_e) {
    // Best-effort; ignore failures to avoid breaking rendering
  }
}

/**
 * Queries the server for moderation status of images in the given root and
 * applies gating/hidden behavior accordingly.
 */
export async function applyImageModeration(rootElement = document) {
  try {
    const imgs = Array.from(rootElement.querySelectorAll('img.user-image[data-image-url]'));
    if (imgs.length === 0) return;
    const urls = Array.from(new Set(imgs.map(img => img.getAttribute('data-image-url')).filter(Boolean)));
    if (urls.length === 0) return;

    const query = encodeURIComponent(urls.join(','));
    let results = [];
    try {
      const response = await window.API.request(`/images/status?urls=${query}`, { silent: true });
      results = response?.results || [];
    } catch (_error) {
      // Best-effort: skip when endpoint unavailable (503 during deploy, etc.)
      return;
    }
    const gated = new Set();
    const hidden = new Map(); // Map URL to hideReason
    for (const r of results) {
      if (r.hidden) {
        hidden.set(r.url, r.hideReason || 'pending_review');
      } else if (r.gated) {
        gated.add(r.url);
      }
    }

    // Hide fully hidden images
    imgs.forEach((img) => {
      const url = img.getAttribute('data-image-url');
      if (hidden.has(url)) {
        const hideReason = hidden.get(url);
        const placeholder = document.createElement('span');
        placeholder.className = 'image-blocked';
        placeholder.style.color = 'var(--text-muted)';
        placeholder.style.fontStyle = 'italic';

        // Set message based on reason
        if (hideReason === 'rejected') {
          placeholder.textContent = '[Image blocked by admins]';
        } else if (hideReason === 'blocked_domain') {
          placeholder.textContent = '[Image from blocked domain]';
        } else {
          placeholder.textContent = '[Image under review]';
        }

        img.replaceWith(placeholder);
      } else if (gated.has(url)) {
        img.setAttribute('data-gated', 'true');
      }
    });

    // Apply gating wrappers after attributes are set
    applyImageGating(rootElement);
    // Attach flagging UI on visible images
    enableImageFlagging(rootElement);
  } catch (_e) {
    // No-op on failure
  }
}

export function enableImageFlagging(rootElement = document) {
  try {
    const images = rootElement.querySelectorAll('img.user-image[data-image-url]');
    images.forEach((img) => {
      if (img.nextElementSibling && img.nextElementSibling.classList?.contains('flag-image-btn')) return;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'flag-image-btn';
      btn.textContent = '🚩 Flag image';
      btn.style.margin = '0.25rem 0';
      btn.style.display = 'inline-block';
      btn.style.fontSize = '0.75rem';
      btn.style.background = 'transparent';
      btn.style.border = '1px solid var(--border-color)';
      btn.style.borderRadius = '4px';
      btn.style.color = 'var(--text-secondary)';
      btn.style.cursor = 'pointer';
      btn.style.width = '100%';
      btn.style.height = '100%';
      btn.addEventListener('click', async (evt) => {
        try {
          evt.stopImmediatePropagation();
          const reason = await promptForReason();
          if (!reason) return;
          const imageUrl = img.getAttribute('data-image-url');
          const postId = findNearestPostId(img);
          const commentId = findNearestCommentId(img);

          // Build request body, only including IDs if they're valid numbers
          const body = {
            image_url: imageUrl,
            reason
          };
          if (postId && !isNaN(Number(postId))) {
            body.post_id = Number(postId);
          }
          if (commentId && !isNaN(Number(commentId))) {
            body.comment_id = Number(commentId);
          }

          await window.API.request('/images/flag', {
            method: 'POST',
            body: JSON.stringify(body)
          });
          img.setAttribute('data-gated', 'true');

          // If image is already in a gated wrapper, re-show the placeholder
          const existingWrapper = img.closest('.gated-image');
          if (existingWrapper) {
            // Check if placeholder already exists
            let placeholder = existingWrapper.querySelector('.gated-image-placeholder');
            if (!placeholder) {
              // Create and show placeholder
              placeholder = document.createElement('div');
              placeholder.className = 'gated-image-placeholder';
              placeholder.style.cursor = 'pointer';
              placeholder.style.color = 'var(--text-muted)';
              placeholder.style.fontStyle = 'italic';
              placeholder.style.padding = '8px 0';
              placeholder.textContent = '[Image has been flagged by another user. Click to view]';
              placeholder.addEventListener('click', () => {
                placeholder.remove();
                img.style.display = '';
              });
              existingWrapper.insertBefore(placeholder, img);
            }
            // Hide the image
            img.style.display = 'none';
          } else {
            // Not wrapped yet, apply gating normally
            applyImageGating(rootElement);
          }

          showToast('Thanks for your report. This image will be reviewed.');
        } catch (_e) {
          showToast('Failed to submit flag. Please try again.');
        }
      });
      const parent = img.closest('.user-image-container');
      parent.insertAdjacentElement('afterend', btn);
    });
  } catch (_e) {
    // Ignore errors when adding flag buttons
  }
}

function promptForReason() {
  const options = ['inappropriate', 'copyright', 'spam', 'broken'];
  const choice = window.prompt('Flag image for: (inappropriate | copyright | spam | broken)', 'inappropriate');
  if (!choice) return null;
  const normalized = choice.trim().toLowerCase();
  return options.includes(normalized) ? normalized : null;
}

function findNearestPostId(node) {
  let el = node;
  while (el) {
    if (el instanceof HTMLElement) {
      const pid = el.getAttribute('data-post-id');
      if (pid) return pid;
    }
    el = el.parentNode;
  }
  return null;
}

function findNearestCommentId(node) {
  let el = node;
  while (el) {
    if (el instanceof HTMLElement) {
      const cid = el.getAttribute('data-comment-id');
      if (cid) return cid;
    }
    el = el.parentNode;
  }
  return null;
}

function showToast(message) {
  const div = document.createElement('div');
  div.textContent = message;
  div.style.position = 'fixed';
  div.style.bottom = '20px';
  div.style.right = '20px';
  div.style.padding = '0.5rem 0.75rem';
  div.style.background = 'var(--surface-color)';
  div.style.border = '1px solid var(--border-color)';
  div.style.borderRadius = '6px';
  div.style.boxShadow = 'var(--shadow)';
  div.style.zIndex = '10000';
  document.body.appendChild(div);
  setTimeout(() => div.remove(), 2500);
}

function normalizeGatedSet(value) {
  if (!value) return null;
  if (value instanceof Set) return value;
  if (Array.isArray(value)) return new Set(value);
  return null;
}


